<template>
  <section class="filter-group" :class="{ nested: depth > 1 }">
    <div class="group-header">
      <label>
        Coincidencia con
        <select :value="group.match" @change="setMatch($event.target.value)">
          <option value="any">alguna</option>
          <option value="all">todas</option>
          <option value="none">ninguna</option>
        </select>
        de estas condiciones
      </label>
      <button v-if="removable" type="button" class="delete-action" title="Eliminar grupo" aria-label="Eliminar grupo" @click="$emit('remove')">
        <font-awesome-icon icon="trash-alt" />
      </button>
    </div>

    <div v-for="(child, index) in group.children" :key="child.id || index" class="group-child">
      <AdvancedAttributeGroup
        v-if="child.kind === 'group'"
        :group="child"
        :depth="depth + 1"
        :schema="schema"
        :fields="fields"
        :aliases="aliases"
        :distinct-values-fetcher="distinctValuesFetcher"
        removable
        @change="updateChild(index, $event)"
        @apply="$emit('apply')"
        @remove="removeChild(index)"
      />
      <div v-else class="condition-row">
        <div class="field-picker">
          <input
            :value="fieldSearch(index, child.field)"
            role="combobox"
            autocomplete="off"
            placeholder="Buscar atributo…"
            :aria-expanded="openFieldIndex === index ? 'true' : 'false'"
            @focus="openField(index, child.field)"
            @input="searchField(index, $event.target.value)"
            @blur="scheduleCloseFields"
            @keydown.esc="closeFields"
          >
          <div v-if="openFieldIndex === index" class="field-options">
            <button
              v-for="field in filteredFields(index)"
              :key="field"
              type="button"
              :class="{ selected: child.field === field }"
              @mousedown.prevent="selectField(index, field)"
            >{{ aliases[field] || field }}</button>
            <span v-if="!filteredFields(index).length">Sin coincidencias</span>
          </div>
        </div>
        <AttributeFilter
          v-if="schema[child.field]"
          :field-type="schema[child.field]"
          :filter="child"
          :field="child.field"
          :distinct-values-fetcher="distinctValuesFetcher"
          compact
          @change="updateCondition(index, $event)"
          @apply="$emit('apply')"
        />
        <span v-else class="field-help">Seleccione un atributo válido</span>
        <button type="button" class="remove-condition delete-action" title="Eliminar condición" aria-label="Eliminar condición" @click="removeChild(index)">
          <font-awesome-icon icon="trash-alt" />
        </button>
      </div>
    </div>

    <div class="group-actions">
      <button type="button" class="small" :disabled="conditionCount >= 50" @click="addCondition">+ Condición</button>
      <button type="button" class="small" :disabled="depth >= 3 || groupCount >= 20" @click="addGroup">+ Grupo</button>
    </div>
  </section>
</template>

<script>
import AttributeFilter from "./AttributeFilter.vue";
import { cloneFilter, newCondition, newGroup } from "@/services/layers/advancedFilters";

export default {
  name: "AdvancedAttributeGroup",
  components: { AttributeFilter },
  props: {
    group: { type: Object, required: true },
    depth: { type: Number, default: 1 },
    schema: { type: Object, default: () => ({}) },
    fields: { type: Array, default: () => [] },
    aliases: { type: Object, default: () => ({}) },
    removable: { type: Boolean, default: false },
    // Reenviado tal cual al auto-render recursivo (grupos anidados) y a cada
    // AttributeFilter — un subgrupo pertenece a la misma capa que su padre.
    distinctValuesFetcher: { type: Function, default: null },
  },
  data() { return { openFieldIndex: null, fieldQueries: {} }; },
  computed: {
    conditionCount() {
      const count = node => (node.kind === "condition" ? 1 : (node.children || []).reduce((sum, child) => sum + count(child), 0));
      return count(this.group);
    },
    groupCount() {
      const count = node => (node.kind === "group" ? 1 + (node.children || []).reduce((sum, child) => sum + count(child), 0) : 0);
      return count(this.group);
    },
  },
  methods: {
    emitGroup(group) { this.$emit("change", group); },
    setMatch(match) { const next = cloneFilter(this.group); next.match = match; this.emitGroup(next); },
    updateChild(index, child) { const next = cloneFilter(this.group); this.$set(next.children, index, child); this.emitGroup(next); },
    removeChild(index) { const next = cloneFilter(this.group); next.children.splice(index, 1); this.emitGroup(next); },
    addCondition() { const next = cloneFilter(this.group); next.children.push(newCondition()); this.emitGroup(next); },
    addGroup() { const next = cloneFilter(this.group); next.children.push(newGroup(this.depth + 1)); this.emitGroup(next); },
    fieldSearch(index, field) {
      return Object.prototype.hasOwnProperty.call(this.fieldQueries, index)
        ? this.fieldQueries[index]
        : (this.aliases[field] || field || "");
    },
    openField(index, field) {
      this.openFieldIndex = index;
      this.$set(this.fieldQueries, index, this.aliases[field] || field || "");
    },
    searchField(index, query) {
      this.openFieldIndex = index;
      this.$set(this.fieldQueries, index, query);
    },
    filteredFields(index) {
      const query = String(this.fieldQueries[index] || "").trim().toLocaleLowerCase();
      if (!query) return this.fields;
      return this.fields.filter(field => `${this.aliases[field] || ""} ${field}`.toLocaleLowerCase().includes(query));
    },
    selectField(index, field) {
      this.setField(index, field);
      this.$set(this.fieldQueries, index, this.aliases[field] || field);
      this.openFieldIndex = null;
    },
    closeFields() { this.openFieldIndex = null; },
    scheduleCloseFields() { setTimeout(this.closeFields, 0); },
    setField(index, field) {
      const next = cloneFilter(this.group); const condition = next.children[index];
      condition.field = this.fields.includes(field) ? field : "";
      // "En uno de" (el multiselect buscable) como operador por defecto al
      // elegir un atributo — mismo criterio que la fila de filtro rápido de
      // la tabla (AttributesTable.vue::filterFor).
      condition.operator = "in";
      condition.value = [];
      this.emitGroup(next);
    },
    updateCondition(index, filter) {
      const next = cloneFilter(this.group);
      next.children[index] = { ...next.children[index], operator: filter.operator, value: filter.value };
      this.emitGroup(next);
    },
  },
};
</script>

<style scoped>
.filter-group { padding: 10px; border: 1px solid #dbe3ec; border-radius: 5px; background: #fff; }
.filter-group.nested { margin-top: 7px; background: #f8fafc; border-left: 3px solid var(--color-orange); }
.group-header, .group-actions, .condition-row { display: flex; align-items: center; gap: 8px; }
.group-header { justify-content: space-between; margin-bottom: 8px; font-size: .82rem; font-weight: 600; }
.group-header label { display:flex; align-items:center; gap:5px; }
.group-header select { min-height:34px; margin:0; padding:5px 8px; border:1px solid #cbd5e1; border-radius:4px; background:#fff; font-size:.8rem; }
.group-child + .group-child { margin-top: 7px; }
.condition-row { display:grid; grid-template-columns:minmax(0,.8fr) minmax(0,1.4fr) 34px; align-items:start; padding:7px; border-radius:4px; background:#eef3f7; }
.field-picker { position:relative; min-width:0; }
.field-picker input { width:100%; box-sizing:border-box; height:34px; padding:5px 8px; border:1px solid #cbd5e1; border-radius:4px; background:#fff; color:#334155; font-size:.8rem; }
.field-options { position:absolute; z-index:30; top:calc(100% + 2px); left:0; right:0; max-height:190px; overflow:auto; padding:3px; border:1px solid #cbd5e1; border-radius:4px; background:#fff; box-shadow:0 6px 16px rgba(15,23,42,.16); }
.field-options button { display:block; width:100%; padding:7px 8px; border:0; border-radius:3px; background:#fff; color:#334155; cursor:pointer; font-size:.8rem; text-align:left; }
.field-options button:hover,.field-options button.selected { background:#e8f1f8; }
.field-options span { display:block; padding:8px; color:#64748b; font-size:.78rem; }
.condition-row .attribute-filter { flex: 1; min-width: 0; }
.field-help { padding: 9px 7px; color: #64748b; font-size: .8rem; }
.delete-action { display:inline-flex; align-items:center; justify-content:center; width:34px; height:34px; padding:0; border:0; border-radius:4px; background:transparent; color:#b91c1c; cursor:pointer; }
.delete-action:hover { background:#fee2e2; }
.remove-condition { font-size:.9rem; }
.group-actions { margin-top: 8px; }
.small { min-height: 34px; padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 4px; background: #fff; cursor: pointer; font-size: .8rem; }
.small:disabled { opacity: .45; cursor: not-allowed; }
@media (max-width: 600px) { .condition-row { grid-template-columns:1fr 28px; } .field-picker,.condition-row .attribute-filter,.field-help { grid-column:1 / -1; } .remove-condition{grid-column:2;grid-row:1} }
</style>
